import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatINR } from "@/lib/api";
import { Trash2, Plus, User, IndianRupee, Scale, Package, ArrowLeft } from "lucide-react";

const KG_SERVICES = new Set(["Wash & Fold"]);

const emptyItem = () => ({
  item: "",
  service: "Wash & Iron",
  unit: "pcs",
  quantity: 1,
  weight_kg: 0,
  rate: 0,
  amount: 0,
});

const computeAmount = (it) => {
  const rate = Number(it.rate || 0);
  return it.unit === "kg"
    ? Number(it.weight_kg || 0) * rate
    : Number(it.quantity || 0) * rate;
};

const BillNew = () => {
  const nav = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const [billNumber, setBillNumber] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const deliveryDate = new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10);
  const [rates, setRates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingBill, setLoadingBill] = useState(isEdit);
  const [form, setForm] = useState({
    customer_name: "", mobile: "", address: "",
    order_date: today, delivery_date: deliveryDate,
    items: [emptyItem()],
    gst_percent: 0,
    discount_type: "flat", discount_value: 0,
    advance: 0,
    payment_mode: "Cash", status: "Received", notes: "",
  });

  useEffect(() => { api.get("/rates").then((r) => setRates(r.data)); }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/bills/${editId}`).then(({ data }) => {
      setBillNumber(data.bill_number);
      setForm({
        customer_name: data.customer_name,
        mobile: data.mobile,
        address: data.address || "",
        order_date: data.order_date,
        delivery_date: data.delivery_date,
        items: data.items.map((it) => ({
          item: it.item,
          service: it.service,
          unit: it.unit || "pcs",
          quantity: it.quantity,
          weight_kg: it.weight_kg || 0,
          rate: it.rate,
          amount: it.amount,
        })),
        gst_percent: data.gst_percent || 0,
        discount_type: data.discount_type || "flat",
        discount_value: data.discount_value || data.discount || 0,
        advance: data.advance || 0,
        payment_mode: data.payment_mode || "Cash",
        status: data.status || "Received",
        notes: data.notes || "",
      });
      setLoadingBill(false);
    }).catch(() => { toast.error("Bill not found"); nav("/admin/bills"); });
  }, [editId, isEdit, nav]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateItem = (i, patch) => {
    setForm((f) => {
      const items = [...f.items];
      const merged = { ...items[i], ...patch };
      merged.amount = computeAmount(merged);
      items[i] = merged;
      return { ...f, items };
    });
  };

  const addItem = () => set("items", [...form.items, emptyItem()]);
  const removeItem = (i) => set("items", form.items.filter((_, idx) => idx !== i));

  const pickRate = (i, itemName) => {
    const it = form.items[i];
    const match = rates.find((r) => r.item === itemName && r.service === it.service);
    updateItem(i, { item: itemName, ...(match ? { rate: match.rate } : {}) });
  };
  const pickService = (i, service) => {
    const it = form.items[i];
    const match = rates.find((r) => r.item === it.item && r.service === service);
    const nextUnit = KG_SERVICES.has(service) ? "kg" : "pcs";
    updateItem(i, {
      service,
      unit: nextUnit,
      ...(match ? { rate: match.rate } : {}),
    });
  };
  const setUnit = (i, unit) => {
    updateItem(i, { unit });
  };

  const subtotal = useMemo(() => form.items.reduce((s, it) => s + Number(it.amount || 0), 0), [form.items]);
  const gstAmount = useMemo(() => (subtotal * Number(form.gst_percent || 0)) / 100, [subtotal, form.gst_percent]);
  const discountAmount = useMemo(() => {
    const v = Number(form.discount_value || 0);
    const base = subtotal + gstAmount;
    if (form.discount_type === "percent") return Math.min(base, (base * v) / 100);
    return Math.min(base, v);
  }, [form.discount_type, form.discount_value, subtotal, gstAmount]);
  const grandTotal = useMemo(() => Math.max(0, subtotal + gstAmount - discountAmount), [subtotal, gstAmount, discountAmount]);
  const balance = useMemo(() => Math.max(0, grandTotal - Number(form.advance || 0)), [grandTotal, form.advance]);
  const totalPcs = useMemo(() => form.items.reduce((s, it) => s + Number(it.quantity || 0), 0), [form.items]);

  const rateItems = [...new Set(rates.map((r) => r.item))];
  const serviceTypes = ["Wash & Fold", "Wash & Iron", "Dry Cleaning", "Steam Iron", "Shoe Cleaning"];

  const save = async () => {
    if (!form.customer_name || !/^\d{10}$/.test(form.mobile)) { toast.error("Enter customer name and valid mobile"); return; }
    for (const it of form.items) {
      if (!it.item || it.quantity <= 0) { toast.error("Each row needs an item and pcs count"); return; }
      if (it.unit === "kg" && Number(it.weight_kg) <= 0) { toast.error("Enter weight in kg for laundry lines"); return; }
    }
    setSaving(true);
    try {
      const body = { ...form, items: form.items, subtotal, gst_amount: gstAmount, discount: discountAmount, grand_total: grandTotal, balance };
      if (isEdit) {
        const { data } = await api.put(`/bills/${editId}`, body);
        toast.success(`Bill ${data.bill_number} updated`);
        nav(`/admin/bills/${data.id}`);
      } else {
        const { data } = await api.post("/bills", body);
        toast.success(`Bill ${data.bill_number} created`);
        nav(`/admin/bills/${data.id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save bill");
    } finally { setSaving(false); }
  };

  if (loadingBill) return <div className="h-64 bg-slate-100 rounded-xl animate-pulse"/>;

  return (
    <div className="space-y-6" data-testid="bill-new-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Poppins' }}>
            {isEdit ? `Edit Bill ${billNumber}` : "Create New Bill"}
          </h1>
          <p className="text-sm text-slate-500">
            {isEdit ? "Bill number & created date remain unchanged." : "Bill number & date will be auto-generated on save."}
          </p>
        </div>
        {isEdit && (
          <button onClick={() => nav(`/admin/bills/${editId}`)} className="qw-btn-ghost" data-testid="bill-edit-cancel"><ArrowLeft size={16}/>Cancel</button>
        )}
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Customer */}
          <div className="qw-dash-card p-6">
            <div className="flex items-center gap-2 text-slate-900 font-semibold mb-4"><User size={18}/> Customer Details</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="qw-label">Name *</label><input className="qw-input" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="bill-cname"/></div>
              <div><label className="qw-label">Mobile *</label><input maxLength={10} className="qw-input" value={form.mobile} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))} data-testid="bill-cmobile"/></div>
              <div className="md:col-span-2"><label className="qw-label">Address</label><input className="qw-input" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="bill-caddress"/></div>
              <div><label className="qw-label">Order Date</label><input type="date" className="qw-input" value={form.order_date} onChange={(e) => set("order_date", e.target.value)} data-testid="bill-order-date"/></div>
              <div><label className="qw-label">Delivery Date</label><input type="date" className="qw-input" value={form.delivery_date} onChange={(e) => set("delivery_date", e.target.value)} data-testid="bill-delivery-date"/></div>
            </div>
          </div>

          {/* Items */}
          <div className="qw-dash-card p-6" data-testid="bill-items-card">
            <div className="flex items-center justify-between mb-4">
              <div className="text-slate-900 font-semibold">Items</div>
              <button onClick={addItem} className="qw-btn-outline text-sm py-1.5 px-3" data-testid="bill-add-item"><Plus size={14}/> Add Item</button>
            </div>
            <div className="space-y-4">
              {form.items.map((it, i) => {
                const isKg = it.unit === "kg";
                return (
                  <div key={i} className="rounded-xl border border-slate-200 p-4 bg-slate-50/40" data-testid={`bill-item-row-${i}`}>
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-12 md:col-span-3">
                        <label className="qw-label text-xs">Item</label>
                        <input list={`items-list-${i}`} className="qw-input" value={it.item} onChange={(e) => pickRate(i, e.target.value)} placeholder="e.g. Shirt, Laundry mix" data-testid={`bill-item-name-${i}`}/>
                        <datalist id={`items-list-${i}`}>{rateItems.map((r) => <option key={r} value={r}/>)}</datalist>
                      </div>
                      <div className="col-span-6 md:col-span-3">
                        <label className="qw-label text-xs">Service</label>
                        <select className="qw-input" value={it.service} onChange={(e) => pickService(i, e.target.value)} data-testid={`bill-item-service-${i}`}>
                          {serviceTypes.map((s) => <option key={s}>{s}</option>)}
                        </select>
                      </div>
                      <div className="col-span-6 md:col-span-2">
                        <label className="qw-label text-xs">Bill by</label>
                        <div className="inline-flex rounded-lg border border-slate-300 bg-white p-0.5 w-full" data-testid={`bill-item-unit-${i}`}>
                          <button
                            type="button"
                            onClick={() => setUnit(i, "pcs")}
                            className={`flex-1 px-2 py-1.5 text-xs font-semibold rounded-md flex items-center justify-center gap-1 transition-colors ${!isKg ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                            data-testid={`bill-item-unit-pcs-${i}`}
                          ><Package size={12}/>Pcs</button>
                          <button
                            type="button"
                            onClick={() => setUnit(i, "kg")}
                            className={`flex-1 px-2 py-1.5 text-xs font-semibold rounded-md flex items-center justify-center gap-1 transition-colors ${isKg ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                            data-testid={`bill-item-unit-kg-${i}`}
                          ><Scale size={12}/>Kg</button>
                        </div>
                      </div>
                      <div className="col-span-6 md:col-span-2">
                        <label className="qw-label text-xs">Rate ₹{isKg ? "/kg" : ""}</label>
                        <input type="number" min={0} step="0.01" className="qw-input" value={it.rate} onChange={(e) => updateItem(i, { rate: Number(e.target.value) })} placeholder="0" data-testid={`bill-item-rate-${i}`}/>
                      </div>
                      <div className="col-span-6 md:col-span-2">
                        <label className="qw-label text-xs">Amount</label>
                        <div className="qw-input bg-slate-50 text-right font-semibold text-slate-900" data-testid={`bill-item-amount-${i}`}>{formatINR(it.amount)}</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-12 gap-2 items-end mt-3">
                      {isKg && (
                        <div className="col-span-6 md:col-span-3">
                          <label className="qw-label text-xs">Weight (kg) *</label>
                          <input type="number" step="0.1" min={0} className="qw-input" value={it.weight_kg} onChange={(e) => updateItem(i, { weight_kg: Number(e.target.value) })} placeholder="e.g. 6.5" data-testid={`bill-item-weight-${i}`}/>
                        </div>
                      )}
                      <div className={`col-span-6 md:col-span-3`}>
                        <label className="qw-label text-xs">Pieces (Qty)</label>
                        <input type="number" min={1} className="qw-input" value={it.quantity} onChange={(e) => updateItem(i, { quantity: Number(e.target.value) })} placeholder="Total pieces" data-testid={`bill-item-qty-${i}`}/>
                        {isKg && <div className="text-[11px] text-slate-500 mt-1">Used for garment tag printing (one tag per piece).</div>}
                      </div>
                      {form.items.length > 1 && (
                        <div className="col-span-12 md:col-span-6 flex md:justify-end">
                          <button type="button" onClick={() => removeItem(i)} className="text-red-500 text-xs hover:text-red-700 inline-flex items-center gap-1" data-testid={`bill-item-remove-${i}`}>
                            <Trash2 size={12}/>Remove line
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div className="qw-dash-card p-6"><label className="qw-label">Notes</label><textarea rows={2} className="qw-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="bill-notes"/></div>
        </div>

        {/* Summary Sidebar */}
        <div>
          <div className="qw-dash-card p-6 sticky top-20" data-testid="bill-summary">
            <div className="flex items-center gap-2 text-slate-900 font-semibold mb-4"><IndianRupee size={18}/> Bill Summary</div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Total Pieces</span><span className="font-semibold" data-testid="bill-total-pcs">{totalPcs}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-semibold">{formatINR(subtotal)}</span></div>
              <div className="flex justify-between items-center"><span className="text-slate-500">GST %</span>
                <input type="number" min={0} max={100} className="qw-input w-20 py-1.5 text-right" value={form.gst_percent} onChange={(e) => set("gst_percent", Number(e.target.value))} data-testid="bill-gst"/>
              </div>
              <div className="flex justify-between"><span className="text-slate-500">GST Amount</span><span>{formatINR(gstAmount)}</span></div>
              <div>
                <label className="qw-label">Discount</label>
                <div className="flex gap-2 items-center">
                  <div className="inline-flex rounded-lg border border-slate-300 bg-white p-0.5" data-testid="bill-discount-type">
                    <button
                      type="button"
                      onClick={() => set("discount_type", "flat")}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${form.discount_type === "flat" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                      data-testid="bill-discount-flat"
                    >₹ Flat</button>
                    <button
                      type="button"
                      onClick={() => set("discount_type", "percent")}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${form.discount_type === "percent" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                      data-testid="bill-discount-percent"
                    >% Percent</button>
                  </div>
                  <input
                    type="number"
                    min={0}
                    max={form.discount_type === "percent" ? 100 : undefined}
                    step="0.01"
                    className="qw-input flex-1 py-1.5 text-right"
                    value={form.discount_value}
                    onChange={(e) => set("discount_value", Number(e.target.value))}
                    placeholder={form.discount_type === "percent" ? "%" : "₹"}
                    data-testid="bill-discount-value"
                  />
                </div>
                {discountAmount > 0 && (
                  <div className="text-xs text-emerald-600 font-semibold text-right mt-1" data-testid="bill-discount-amount">
                    Applied: − {formatINR(discountAmount)}
                    {form.discount_type === "percent" && ` (${form.discount_value}%)`}
                  </div>
                )}
              </div>
              <div className="border-t border-slate-200 pt-3 flex justify-between text-base"><span className="font-semibold text-slate-900">Grand Total</span><span className="font-bold text-blue-600" data-testid="bill-grand-total">{formatINR(grandTotal)}</span></div>
              <div className="flex justify-between items-center"><span className="text-slate-500">Advance ₹</span>
                <input type="number" min={0} className="qw-input w-24 py-1.5 text-right" value={form.advance} onChange={(e) => set("advance", Number(e.target.value))} data-testid="bill-advance"/>
              </div>
              <div className="flex justify-between font-semibold"><span>Balance</span><span data-testid="bill-balance">{formatINR(balance)}</span></div>
              <div>
                <label className="qw-label mt-2">Payment Mode</label>
                <select className="qw-input" value={form.payment_mode} onChange={(e) => set("payment_mode", e.target.value)} data-testid="bill-payment">
                  {["Cash", "UPI", "Card", "Bank", "Pending"].map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="qw-label">Status</label>
                <select className="qw-input" value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="bill-status">
                  {["Received", "Processing", "Dry Cleaning", "Ironing", "Ready", "Out for Delivery", "Delivered"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <button disabled={saving} onClick={save} className="qw-btn-primary w-full mt-6" data-testid="bill-save">{saving ? "Saving…" : isEdit ? "Update Bill" : "Create Bill"}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillNew;
